import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  deleteDoc,
} from "firebase/firestore";
import { db, auth, storage } from "../../Firebase_Config/firebaseConfig";
import { ref, deleteObject } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import styles from "./MyBlogsBody.module.css";
import { Helmet } from "react-helmet-async";

function MyBlogsBody({ setAlertMsg, setAlertColor }) {
  const [myBlogs, setMyBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!auth.currentUser) {
      setMyBlogs([]);
      setLoading(false);
      return;
    }

    const blogsRef = collection(db, "blogPosts");
    const q = query(blogsRef, where("userId", "==", auth.currentUser.uid));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const blogsData = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        blogsData.sort((a, b) => {
          const timeA = a.publishDate?.seconds
            ? a.publishDate.seconds * 1000
            : new Date(a.publishDate).getTime();
          const timeB = b.publishDate?.seconds
            ? b.publishDate.seconds * 1000
            : new Date(b.publishDate).getTime();
          return timeB - timeA;
        });
        setMyBlogs(blogsData);
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setAlertMsg("Error fetching blogs");
        setAlertColor("red");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [setAlertMsg, setAlertColor]);

  const handleDelete = async (blog) => {
    try {
      if (blog.ImgPath) {
        const imageRef = ref(storage, blog.ImgPath);
        await deleteObject(imageRef);
      }
      await deleteDoc(doc(db, "blogPosts", blog.id));
      setAlertMsg("🗑️ Blog deleted successfully!");
      setAlertColor("red");
    } catch (error) {
      console.error(error);
      setAlertMsg("❌ Failed to delete blog.");
      setAlertColor("red");
    }
  };

  const handleEdit = (blogId) => {
    navigate(`/EditBlog/${blogId}`);
  };

  return (
    <>
      <Helmet>
        <title>My Blogs | VALSCO</title>
        <meta
          name="description"
          content="Your blogs that you posted on Valsco Technology's platform."
        />
      </Helmet>

      {/* Heading always visible */}
      <div style={{ paddingTop: "7rem",}}>
        <h2
          style={{
          fontSize: "2.5rem",
          fontWeight: "bold",
          marginBottom: "1.5rem",
          textAlign: "center",
          }}
        >
          Welcome to Your Blogs !
        </h2>
      </div>

      {loading ? (
        <p
          style={{
            textAlign: "center",
            fontSize: "1.25rem",
            fontWeight: "bold",
            marginTop: "2rem",
          }}
        >
          Loading your blogs...
        </p>
      ) : myBlogs.length === 0 ? (
        <p
          style={{
            textAlign: "center",
            fontSize: "1.5rem",
            marginTop: "15rem",
          }}
        >
          No blogs created yet.
        </p>
      ) : (
        <div className={styles.myBlogsSection}>
          {myBlogs.map((blog) => (
            <div className={styles.myBlogsCard} key={blog.id}>
              {/* Top content: Title + Body */}
              <div className={styles.myBlogsContent}>
                <h3 className={styles.myBlogsCardTitle}>{blog.title}</h3>
                {blog.postText && (
                  <p className={styles.myBlogsCardDesc} style={{ margin: 0 }}>
                    {blog.postText.length > 150
                      ? blog.postText.slice(0, 150) + "..."
                      : blog.postText}
                  </p>
                )}
              </div>

              {/* Footer: Author, Status, Buttons */}
              <div className={styles.myBlogsCardFooter}>
                <p className={styles.myBlogsCardAuthor} style={{ margin: 0 }}>
                  ✍️ By: {blog.author?.name || "Anonymous"}
                </p>

                <p
                  className={`${styles.myBlogsCardStatus} ${
                    blog.status === "pending"
                      ? styles.pending
                      : blog.status === "approved"
                      ? styles.approved
                      : styles.rejected
                  }`}
                  style={{ margin: 0 }}
                >
                  {blog.status === "pending"
                    ? "Pending"
                    : blog.status === "approved"
                    ? "Approved"
                    : "Rejected"}
                </p>

                <div className={styles.myBlogsCardBtnDiv}>
                  {/* View Button (always) */}
                  <button
                    className={`${styles.myBlogsCardBtn} ${styles.viewBtn}`}
                    onClick={() => navigate(`/BlogPage/${blog.id}`)}
                  >
                    <span className={styles.btnText}>View</span> 👁️
                  </button>

                  {/* Edit only if Pending */}
                  {blog.status === "pending" && (
                    <button
                      className={`${styles.myBlogsCardBtn} ${styles.editBtn}`}
                      onClick={() => handleEdit(blog.id)}
                    >
                      <span className={styles.btnText}>Edit</span> ✏️
                    </button>
                  )}

                  {/* Delete if Pending, Approved, or Rejected */}
                  {(blog.status === "pending" ||
                    blog.status === "approved" ||
                    blog.status === "rejected") && (
                    <button
                      className={`${styles.myBlogsCardBtn} ${styles.deleteBtn}`}
                      onClick={() => handleDelete(blog)}
                    >
                      <span className={styles.btnText}>Delete</span> 🗑️
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export default MyBlogsBody;
